import React from 'react';
import PropTypes from 'prop-types';
import './Card.css';

/**
 * Card Component
 * 
 * A flexible container component for displaying content in a card format.
 * 
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Card content
 * @param {React.ReactNode} props.header - Optional header content
 * @param {React.ReactNode} props.footer - Optional footer content
 * @param {string} props.image - Optional image URL
 * @param {string} props.imageAlt - Alt text for image
 * @param {string} props.imagePosition - Image position: 'top' | 'left' | 'right' | 'background'
 * @param {React.ReactNode} props.badge - Optional badge/tag in corner
 * @param {string} props.variant - Card style variant: 'default' | 'outlined' | 'elevated' | 'interactive'
 * @param {boolean} props.hoverable - Enable hover effects
 * @param {function} props.onClick - Click handler (makes card clickable)
 * @param {string} props.className - Additional CSS classes
 */
const Card = ({
  children,
  header = null,
  footer = null,
  image = null,
  imageAlt = '',
  imagePosition = 'top',
  badge = null,
  variant = 'default',
  hoverable = false,
  onClick = null,
  className = '',
  ...props
}) => {
  const isClickable = !!onClick || hoverable;

  const cardClasses = [
    'card',
    `card--${variant}`,
    image && imagePosition !== 'background' ? `card--image-${imagePosition}` : '',
    imagePosition === 'background' ? 'card--background-image' : '',
    isClickable ? 'card--clickable' : '',
    className
  ].filter(Boolean).join(' ');

  const handleClick = () => {
    if (onClick) {
      onClick();
    }
  };

  const handleKeyDown = (event) => {
    if (onClick && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      onClick();
    }
  };

  const cardContent = (
    <>
      {/* Badge */}
      {badge && (
        <div className="card__badge">
          {badge}
        </div>
      )}

      {/* Background Image */}
      {image && imagePosition === 'background' && (
        <div 
          className="card__background-image"
          style={{ backgroundImage: `url(${image})` }}
          aria-hidden="true"
        />
      )}

      {/* Top/Left/Right Image */}
      {image && imagePosition !== 'background' && (
        <div className="card__image-container">
          <img 
            src={image} 
            alt={imageAlt} 
            className="card__image"
          />
        </div>
      )}

      <div className="card__content-wrapper">
        {/* Header */}
        {header && (
          <div className="card__header">
            {header}
          </div>
        )}

        {/* Body */}
        <div className="card__body">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div className="card__footer">
            {footer}
          </div>
        )}
      </div>
    </>
  );

  // Render as button if clickable
  if (onClick) {
    return (
      <div
        className={cardClasses}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        role="button"
        tabIndex={0}
        {...props}
      >
        {cardContent}
      </div>
    );
  }

  // Render as div otherwise
  return (
    <div className={cardClasses} {...props}>
      {cardContent}
    </div>
  );
};

Card.propTypes = {
  children: PropTypes.node.isRequired,
  header: PropTypes.node,
  footer: PropTypes.node,
  image: PropTypes.string,
  imageAlt: PropTypes.string,
  imagePosition: PropTypes.oneOf(['top', 'left', 'right', 'background']),
  badge: PropTypes.node,
  variant: PropTypes.oneOf(['default', 'outlined', 'elevated', 'interactive']),
  hoverable: PropTypes.bool,
  onClick: PropTypes.func,
  className: PropTypes.string,
};

export default Card;
